import React, { useCallback, useRef, useState } from "react"
import { showNotification, updateNotification } from "@mantine/notifications"
import { IconCheck, IconX } from "@tabler/icons-react"
import { doc, setDoc, FirestoreError, onSnapshot, Unsubscribe } from "firebase/firestore"
import { Anchor, Text } from "@mantine/core"
import { Link } from "react-router-dom"
import { httpsCallable } from "firebase/functions"

import { useLanguage } from "../contexts/language-context"
import { firebase } from "../services/firebase/firebase"
import { AwsIoTMQTTCommandData } from "../functions/aws/iot/aws-iot-mqttCommand"
import { DeviceDocument } from "../contexts/devices-context"

export const useMQTTCommand = (
    topic: string,
    organisation: string,
    group: string,
    device: DeviceDocument,
    message = "",
) => {
    const { L } = useLanguage()
    const [processing, setProcessing] = useState(false)
    const unsubscribeRef = useRef<Unsubscribe | null>(null)

    const createToken = () =>
        `${topic.replaceAll("/", "-")}-${Math.floor(Math.random() * 16 ** 6).toString(16)}`

    const showError = useCallback(
        (token: string, message?: string) => (error?: FirestoreError | Error) => {
            setProcessing(false)

            updateNotification({
                id: token,
                title: L("Command failed!"),
                message: (
                    <Text color="dimmed">
                        {message || error?.message || L("Oops, something went wrong")}
                        <br />
                        {L(
                            "If this error persists, please contact %s.",
                            <Anchor component={Link} to="mailto:dev@ecomoni.nl">
                                {L("Ecomoni support")}
                            </Anchor>,
                        )}
                    </Text>
                ),
                icon: <IconX size={18} />,
                autoClose: 2000,
            })

            console.error(error)
        },
        [L],
    )

    const send = useCallback(async () => {
        if (processing) return

        const token = createToken()

        unsubscribeRef.current?.()
        unsubscribeRef.current = null

        setProcessing(true)

        showNotification({
            id: token,
            title: topic,
            message: L("Command has been sent."),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        })

        const ref = doc(
            firebase.firestore,
            `organisations/${organisation}/things/${device.id}/commands/${token}`,
        )

        try {
            await setDoc(ref, {
                device: device.id,
                status: "pending",
                response: "",
            })

            unsubscribeRef.current = onSnapshot(
                ref,
                snapshot => {
                    const command = snapshot.data()

                    if (!command) return

                    if (command.status === "success") {
                        unsubscribeRef.current?.()
                        unsubscribeRef.current = null

                        setProcessing(false)

                        updateNotification({
                            id: token,
                            title: L("Command successful!"),
                            message: command.response,
                            icon: <IconCheck size={18} />,
                            autoClose: 2000,
                        })
                    }

                    if (command.status === "error") {
                        unsubscribeRef.current?.()
                        unsubscribeRef.current = null

                        showError(token, command.message)()
                    }
                },
                showError(token),
            )

            await httpsCallable<AwsIoTMQTTCommandData>(
                firebase.functions,
                "aws-iot-mqttCommand",
            )({
                group,
                organisation,
                device,
                topic,
                message,
                token,
            })
        } catch (error) {
            unsubscribeRef.current?.()
            unsubscribeRef.current = null

            showError(token)(error as Error)
        }
    }, [processing, topic, organisation, group, device, message, L, showError])

    return { processing, send }
}